import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Tooltip,
  CircularProgress, Snackbar, Alert, Chip, Checkbox, FormControlLabel, Divider,
  InputAdornment, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, SwapVert as MovIcon, Close as CloseIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const MOVEMENT_TYPES = [
  { value: 'in', label: 'Entrada', color: '#10b981' },
  { value: 'out', label: 'Saída', color: '#ef4444' },
  { value: 'adjustment', label: 'Ajuste', color: '#3b82f6' },
  { value: 'return', label: 'Devolução', color: '#f59e0b' },
  { value: 'loss', label: 'Perda', color: '#dc2626' },
  { value: 'expired', label: 'Vencido', color: '#6b7280' },
];

const MOTIVOS_ENTRADA = ['Compra', 'Doação', 'Transferência', 'Devolução de paciente', 'Bonificação', 'Ajuste de inventário', 'Outro'];
const MOTIVOS_SAIDA = ['Uso em procedimento', 'Dispensação ao paciente', 'Perda', 'Vencido', 'Transferência', 'Ajuste de inventário', 'Outro'];

const getTypeInfo = (t: string) => MOVEMENT_TYPES.find(m => m.value === t) || { label: t, color: '#6b7280' };
const formatDate = (d: string) => d ? new Date(d).toLocaleString('pt-BR') : '-';
const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const emptyForm = {
  item_id: '', movement_type: 'in', quantity: 1, lot_number: '', expiration_date: '',
  notes: '', performed_by: '', unit_cost: 0, total_cost: 0,
  invoice_number: '', supplier_name: '', patient_name: '', reason: '',
  location_id: '', individual_code: '',
  launch_payable: false, launch_patient_account: false,
};

const InventoryMovements: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [movements, setMovements] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [allItems, setAllItems] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const p: Record<string, any> = { page: page + 1, limit: rowsPerPage };
      if (typeFilter) p.movement_type = typeFilter;
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
      const res = await axios.get(getApiUrl('/inventory-module/movements'), { params: p });
      setMovements(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch { } finally { setLoading(false); }
  }, [page, rowsPerPage, typeFilter, dateFrom, dateTo]);

  const fetchMeta = useCallback(async () => {
    try {
      const [itemsRes, locsRes, patientsRes] = await Promise.all([
        axios.get(getApiUrl('/inventory-module/items'), { params: { limit: 500 } }),
        axios.get(getApiUrl('/inventory-module/locations')),
        axios.get(getApiUrl('/clinic/patients'), { params: { limit: 200 } }).catch(() => ({ data: { data: [] } })),
      ]);
      setAllItems(itemsRes.data.data || []);
      setLocations(locsRes.data.data || []);
      setPatients(Array.isArray(patientsRes.data.data) ? patientsRes.data.data : Array.isArray(patientsRes.data) ? patientsRes.data : []);
    } catch { }
  }, []);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const isEntry = form.movement_type === 'in' || form.movement_type === 'return';
  const isExit = form.movement_type === 'out' || form.movement_type === 'loss' || form.movement_type === 'expired';

  const handleSave = async () => {
    if (!form.item_id || !form.movement_type || !form.quantity) { setSnackbar({ open: true, message: 'Preencha item, tipo e quantidade', severity: 'error' }); return; }
    if (!form.reason) { setSnackbar({ open: true, message: 'Motivo é obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      const payload: any = {
        item_id: form.item_id, movement_type: form.movement_type, quantity: Number(form.quantity),
        lot_number: form.lot_number || null, expiration_date: form.expiration_date || null,
        performed_by: form.performed_by || null, unit_cost: Number(form.unit_cost) || null,
        total_cost: Number(form.total_cost) || null,
        notes: [form.reason, form.notes, form.invoice_number ? `NF: ${form.invoice_number}` : '', form.supplier_name ? `Fornecedor: ${form.supplier_name}` : '', form.patient_name ? `Paciente: ${form.patient_name}` : ''].filter(Boolean).join(' | '),
      };
      await axios.post(getApiUrl('/inventory-module/movements'), payload);
      setSnackbar({ open: true, message: 'Movimentação registrada!', severity: 'success' });
      setDialogOpen(false);
      setForm({ ...emptyForm });
      fetchMovements();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
    finally { setSaving(false); }
  };

  const openNewDialog = (type: string = 'in') => {
    setForm({ ...emptyForm, movement_type: type });
    setDialogOpen(true);
  };

  const selectedItem = allItems.find(i => i.id === form.item_id);
  const dialogTitle = isEntry ? 'Lançamento de Entrada' : isExit ? 'Lançamento de Saída' : 'Nova Movimentação';
  const dialogSubtitle = selectedItem ? `» ${selectedItem.name}${selectedItem.storage_location ? ` (${selectedItem.storage_location})` : ''}` : '';

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MovIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Movimentações de Estoque</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar"><IconButton onClick={fetchMovements}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openNewDialog('in')}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            Nova Movimentação
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth><InputLabel>Tipo</InputLabel>
              <Select value={typeFilter} label="Tipo" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {MOVEMENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField size="small" fullWidth type="date" label="De" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField size="small" fullWidth type="date" label="Até" value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Button variant="outlined" onClick={fetchMovements} fullWidth sx={{ textTransform: 'none', fontWeight: 600 }}>Filtrar</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Quantidade</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Anterior</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Novo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Lote</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Valor Unit.</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Responsável</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Detalhes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: '#9ca3af' }}>Nenhuma movimentação encontrada</TableCell></TableRow>
              ) : movements.map(mov => {
                const typeInfo = getTypeInfo(mov.movement_type);
                return (
                  <TableRow key={mov.id} hover>
                    <TableCell><Typography variant="caption">{formatDate(mov.created_at)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{mov.clinic_inventory_items?.name || '-'}</Typography></TableCell>
                    <TableCell><Chip label={typeInfo.label} size="small" sx={{ bgcolor: `${typeInfo.color}22`, color: typeInfo.color, fontWeight: 600 }} /></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 700 }}>{mov.quantity}</Typography></TableCell>
                    <TableCell align="right">{mov.previous_stock ?? '-'}</TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600 }}>{mov.new_stock ?? '-'}</Typography></TableCell>
                    <TableCell>{mov.lot_number || '-'}</TableCell>
                    <TableCell>{mov.unit_cost ? formatCurrency(mov.unit_cost) : '-'}</TableCell>
                    <TableCell>{mov.performed_by || '-'}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mov.notes || '-'}</Typography></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Por página" />
        </TableContainer>
      )}

      {/* Dialog Lançamento Entrada / Saída / Movimentação */}
      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{dialogTitle}</Typography>
            {dialogSubtitle && <Typography variant="caption" color="text.secondary">{dialogSubtitle}</Typography>}
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small"><InputLabel>Item *</InputLabel>
                <Select value={form.item_id} label="Item *" onChange={(e) => setForm({ ...form, item_id: e.target.value })}>
                  {allItems.map(item => <MenuItem key={item.id} value={item.id}>{item.name} ({item.sku || '-'})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Tipo *</InputLabel>
                <Select value={form.movement_type} label="Tipo *" onChange={(e) => setForm({ ...form, movement_type: e.target.value })}>
                  {MOVEMENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth required label="Quantidade *" type="number" value={form.quantity}
                onChange={(e) => {
                  const qty = Number(e.target.value);
                  setForm({ ...form, quantity: qty, total_cost: qty * form.unit_cost });
                }}
                size="small" inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label={isEntry ? 'Valor de Compra' : 'Valor de Venda'} type="number"
                value={form.unit_cost}
                onChange={(e) => {
                  const uc = Number(e.target.value);
                  setForm({ ...form, unit_cost: uc, total_cost: form.quantity * uc });
                }}
                size="small" InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Data" type="date" value={form.expiration_date ? '' : ''}
                size="small" InputLabelProps={{ shrink: true }}
                defaultValue={new Date().toISOString().slice(0, 10)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Nota Fiscal" value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Localização</InputLabel>
                <Select value={form.location_id} label="Localização" onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                  <MenuItem value="">Selecione</MenuItem>
                  {locations.filter(l => l.is_active).map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Responsável" value={form.performed_by}
                onChange={(e) => setForm({ ...form, performed_by: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Lote" value={form.lot_number}
                onChange={(e) => setForm({ ...form, lot_number: e.target.value })} size="small" />
            </Grid>

            {isEntry && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Fornecedor" value={form.supplier_name}
                  onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} size="small" />
              </Grid>
            )}

            <Grid item xs={12} sm={isEntry ? 6 : 6}>
              <TextField fullWidth label="Validade" type="date" value={form.expiration_date}
                onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código Individual" value={form.individual_code}
                onChange={(e) => setForm({ ...form, individual_code: e.target.value })} size="small" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Motivo *</InputLabel>
                <Select value={form.reason} label="Motivo *" onChange={(e) => setForm({ ...form, reason: e.target.value })}>
                  <MenuItem value="">Selecione</MenuItem>
                  {(isEntry ? MOTIVOS_ENTRADA : MOTIVOS_SAIDA).map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Paciente" value={form.patient_name}
                onChange={(e) => setForm({ ...form, patient_name: e.target.value })} size="small"
                placeholder="Opcional" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Observações" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} size="small" multiline rows={2} />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
            </Grid>

            {isEntry && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.launch_payable} onChange={(e) => setForm({ ...form, launch_payable: e.target.checked })} />}
                  label={<Typography variant="body2">Lançar no contas a pagar</Typography>}
                />
              </Grid>
            )}
            {isExit && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.launch_patient_account} onChange={(e) => setForm({ ...form, launch_patient_account: e.target.checked })} />}
                  label={<Typography variant="body2">Lançar na conta do paciente</Typography>}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryMovements;
